// Site-wide content search index.
//
// Each entry references i18n keys (not hard-coded text), so search works in
// English, Thai, and Lao automatically — both the displayed result and the
// searchable text come from the active language via `t()`.
//
// Fields:
//   id        unique identifier for the entry
//   path      route to navigate to when selected
//   hash      optional anchor id on the target page to scroll to (no leading #)
//   groupKey  i18n key for the page/group label shown in results
//   titleKey  i18n key for the result title
//   descKey   i18n key for the result description/snippet
//   extraKeys optional i18n keys whose translated text is added to the haystack
//             (string keys, or [key, count] for indexed lists like timeline)

const SEARCH_ENTRIES = [
  // ---- Top-level pages -------------------------------------------------
  {
    id: "page-home",
    path: "/",
    groupKey: "search.groups.pages",
    titleKey: "navigation.home",
    descKey: "home.heroDescription",
    extraKeys: ["home.subtitle"],
  },
  {
    id: "page-track",
    path: "/track",
    groupKey: "search.groups.pages",
    titleKey: "navigation.trackPackage",
    descKey: "tracking.description",
    extraKeys: ["tracking.inputHint"],
  },
  {
    id: "page-calculator",
    path: "/calculator",
    groupKey: "search.groups.pages",
    titleKey: "navigation.calculator",
    descKey: "home.calculator.description",
  },
  {
    id: "page-services",
    path: "/services",
    groupKey: "search.groups.pages",
    titleKey: "navigation.services",
    descKey: "services.subtitle",
  },
  {
    id: "page-about",
    path: "/about",
    groupKey: "search.groups.pages",
    titleKey: "navigation.about",
    descKey: "about.subtitle",
  },
  {
    id: "page-contact",
    path: "/contact",
    groupKey: "search.groups.pages",
    titleKey: "navigation.contact",
    descKey: "contact.subtitle",
  },

  // ---- Services --------------------------------------------------------
  {
    id: "svc-cross-border",
    path: "/services",
    hash: "svc-cross-border",
    groupKey: "search.groups.services",
    titleKey: "services.crossBorder.title",
    descKey: "services.crossBorder.description",
    extraKeys: [
      "services.crossBorder.price",
      "services.crossBorder.f1",
      "services.crossBorder.f2",
      "services.crossBorder.f3",
      "services.crossBorder.f4",
    ],
  },
  {
    id: "svc-claim",
    path: "/services",
    hash: "svc-claim",
    groupKey: "search.groups.services",
    titleKey: "services.claimProtection.title",
    descKey: "services.claimProtection.description",
    extraKeys: [
      "services.claimProtection.f1",
      "services.claimProtection.f2",
      "services.claimProtection.f3",
      "services.claimProtection.f4",
    ],
  },
  {
    id: "svc-home-delivery",
    path: "/services",
    hash: "svc-home-delivery",
    groupKey: "search.groups.services",
    titleKey: "services.homeDelivery.title",
    descKey: "services.homeDelivery.description",
    extraKeys: [
      "services.homeDelivery.f1",
      "services.homeDelivery.f2",
      "services.homeDelivery.f3",
      "services.homeDelivery.f4",
    ],
  },
  {
    id: "svc-english",
    path: "/services",
    hash: "svc-english",
    groupKey: "search.groups.services",
    titleKey: "services.englishSupport.title",
    descKey: "services.englishSupport.description",
    extraKeys: [
      "services.englishSupport.f1",
      "services.englishSupport.f2",
      "services.englishSupport.f3",
      "services.englishSupport.f4",
    ],
  },

  // ---- About -----------------------------------------------------------
  {
    id: "about-mission",
    path: "/about",
    groupKey: "search.groups.about",
    titleKey: "about.mission.title",
    descKey: "about.mission.description",
  },
  {
    id: "about-vision",
    path: "/about",
    groupKey: "search.groups.about",
    titleKey: "about.vision.title",
    descKey: "about.vision.description",
  },
  {
    id: "about-team",
    path: "/about",
    groupKey: "search.groups.about",
    titleKey: "about.teamTitle",
    descKey: "about.teamSubtitle",
  },
  {
    id: "about-timeline",
    path: "/about",
    groupKey: "search.groups.about",
    titleKey: "about.timeline.title",
    descKey: "about.timeline.subtitle",
  },

  // ---- Contact / FAQ ---------------------------------------------------
  {
    id: "contact-form",
    path: "/contact",
    hash: "contact-form",
    groupKey: "search.groups.contact",
    titleKey: "contact.form.title",
    descKey: "contact.form.subtitle",
  },
  {
    id: "contact-branches",
    path: "/contact",
    hash: "contact-branches",
    groupKey: "search.groups.contact",
    titleKey: "contact.branches.title",
    descKey: "contact.branches.subtitle",
    extraKeys: [
      "contact.branches.phontong",
      "contact.branches.thongkang",
      "contact.branches.dongkhamxang",
      "contact.branches.savannakhet",
    ],
  },
  {
    id: "faq-delivery",
    path: "/contact",
    hash: "contact-faq",
    groupKey: "search.groups.faq",
    titleKey: "contact.faq.deliveryTimes.question",
    descKey: "contact.faq.deliveryTimes.answer",
  },
  {
    id: "faq-tracking",
    path: "/contact",
    hash: "contact-faq",
    groupKey: "search.groups.faq",
    titleKey: "contact.faq.tracking.question",
    descKey: "contact.faq.tracking.answer",
  },
  {
    id: "faq-payment",
    path: "/contact",
    hash: "contact-faq",
    groupKey: "search.groups.faq",
    titleKey: "contact.faq.payment.question",
    descKey: "contact.faq.payment.answer",
  },
  {
    id: "faq-claim",
    path: "/contact",
    hash: "contact-faq",
    groupKey: "search.groups.faq",
    titleKey: "contact.faq.freeClaim.question",
    descKey: "contact.faq.freeClaim.answer",
  },
];

// Languages whose text is folded into the haystack so users can search in any
// language regardless of the UI language they're currently viewing.
const SEARCHABLE_LANGUAGES = ["en", "th", "lo"];

// Resolve all the searchable text of an entry for a single language.
const entryTextForLang = (tl, entry) => {
  const parts = [tl(entry.titleKey, "")];
  if (entry.descKey) parts.push(tl(entry.descKey, ""));
  (entry.extraKeys || []).forEach((k) => {
    if (typeof k === "string") parts.push(tl(k, ""));
  });
  return parts.join(" ");
};

/**
 * Build a ready-to-search index.
 *
 * Display fields (`title`, `description`, `group`) use the active language via
 * `t`, while `haystack` concatenates the entry's text across ALL languages so a
 * query in Thai or Lao matches even while the site is shown in English.
 *
 * @param {Function} t      active-language translator (from useTranslation)
 * @param {object}   i18n   the i18next instance (for per-language lookups)
 */
export function buildSearchIndex(t, i18n) {
  // One translator per searchable language. Falls back to `t` if unavailable.
  const translators = SEARCHABLE_LANGUAGES.map((lng) =>
    i18n && typeof i18n.getFixedT === "function" ? i18n.getFixedT(lng) : t
  );

  return SEARCH_ENTRIES.map((entry) => {
    const title = t(entry.titleKey, "");
    const description = entry.descKey ? t(entry.descKey, "") : "";
    const haystack = translators
      .map((tl) => entryTextForLang(tl, entry))
      .join(" ")
      .toLowerCase();
    return {
      id: entry.id,
      path: entry.path,
      hash: entry.hash || null,
      group: t(entry.groupKey, ""),
      title,
      description,
      haystack,
    };
  });
}

/**
 * Search the index for `query`. Returns matching entries sorted by relevance.
 * An entry matches when every whitespace-separated token in the query is
 * found somewhere in its haystack. Title hits are weighted higher.
 */
export function searchSite(index, query, limit = 12) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);

  const results = [];
  for (const entry of index) {
    const titleLc = entry.title.toLowerCase();
    let score = 0;
    let matchedAll = true;
    for (const token of tokens) {
      if (!entry.haystack.includes(token)) {
        matchedAll = false;
        break;
      }
      if (titleLc.startsWith(token)) score += 4;
      else if (titleLc.includes(token)) score += 3;
      else score += 1;
    }
    if (matchedAll) results.push({ ...entry, score });
  }

  results.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));
  return results.slice(0, limit);
}
